import * as React from "react";
import {useState} from "react";
import {getActiveRules, moveRule, selectRules} from "../slices/ruleSlice";
import {useDispatch, useSelector} from "react-redux";
import {Popup, Icon} from "semantic-ui-react";
import "./RuleList.css"
import {getRuleToDisplay, setRuleToDisplay} from "../slices/rightMenuSlice";
import useKeypress from "../misc/hooks/use_keypress";
import {queryNextRule} from "../slices/tableSlice";
import {GenericXlsExtractionRule} from "../../../../../generated";

interface RuleListProps {

}

export function RuleList(props:React.PropsWithChildren<RuleListProps>){
    const rules = useSelector(selectRules).filter(r=>r.parentRuleId == undefined)
    const allRules = useSelector(selectRules)
    const selectedRule: GenericXlsExtractionRule|null = useSelector(getRuleToDisplay)
    const nextRuleUp = useSelector(queryNextRule("UP"))
    const nextRuleDown = useSelector(queryNextRule("DOWN"))
    const activeRules = useSelector(getActiveRules)

    const [searchText, setSearchText] = React.useState("")
    const [showDetails, setShowDetails] = React.useState(false)

    const dispatch = useDispatch()

    const arrowUp = ()=>{ nextRuleUp && dispatch(setRuleToDisplay(nextRuleUp)) }
    const arrowDown = ()=>{ nextRuleDown && dispatch(setRuleToDisplay(nextRuleDown)) }

    useKeypress({
        "ArrowUp": arrowUp,
        "ArrowDown": arrowDown
    })

    const matchingIds = React.useMemo(() => {
        const ids = new Set<number>()
        if (!searchText.trim()) return ids
        const term = searchText.toLowerCase()
        allRules.forEach(r => {
            if ((r.tagName ?? "").toLowerCase().includes(term) ||
                (r.selectionLabel ?? "").toLowerCase().includes(term) ||
                (r.matchText ?? "").toLowerCase().includes(term)) {
                ids.add(r.ruleId)
            }
        })
        return ids
    }, [allRules, searchText])

    const filteredRootRules = React.useMemo(() => {
        if (!searchText.trim()) return rules
        function hasMatch(r: GenericXlsExtractionRule): boolean {
            if (matchingIds.has(r.ruleId)) return true
            return allRules.filter(c => c.parentRuleId === r.ruleId).some(c => hasMatch(c))
        }
        const filtered = rules.filter(r => hasMatch(r))
        return filtered.length > 0 ? filtered : rules
    }, [rules, allRules, searchText, matchingIds])

    return <ul className="RuleList">
        <li className="RuleListHeader" onClick={()=>dispatch(setRuleToDisplay(null))}>
            {activeRules=="spt" ? "Split" : "Extraction"} Rules
        </li>
        <li className="RuleListSearch">
            <Icon name="search" style={{color: '#999'}} size="small"/>
            <input type="text" placeholder="Search by Tag or Selection Label" value={searchText} onChange={e => setSearchText(e.target.value)}/>
            <label><input type="checkbox" checked={showDetails} onChange={e => setShowDetails(e.target.checked)}/> Details</label>
        </li>
        <HierarchicalRuleList parent={null} level={0} rules={filteredRootRules} selectedRule={selectedRule} showDetails={showDetails} matchingIds={matchingIds}/>
    </ul>
}

interface HierarchicalRuleListProps {
    level: number
    parent: GenericXlsExtractionRule|null
    rules: GenericXlsExtractionRule[]
    selectedRule: GenericXlsExtractionRule|null
    showDetails: boolean
    matchingIds: Set<number>
}

function HierarchicalRuleList(props:React.PropsWithChildren<HierarchicalRuleListProps>){
    return <ul className={`RuleList level`}>
        {
            props.rules.map((x,idx)=>
            <RuleListItem key={idx} rule={x} selectedRule={props.selectedRule} level={props.level} showDetails={props.showDetails} matchingIds={props.matchingIds}/>
        )}
    </ul>
}

interface RuleListItemProps{
    rule: GenericXlsExtractionRule
    selectedRule: GenericXlsExtractionRule|null
    level: number
    showDetails: boolean
    matchingIds: Set<number>
}

interface RuleListItemProps{
    rule: GenericXlsExtractionRule
    selectedRule: GenericXlsExtractionRule|null
    level: number
}

export function RuleListItem(props: React.PropsWithChildren<RuleListItemProps>){
    const isSelected = props.rule == props.selectedRule
    const dispatch = useDispatch()

    const [isDraggedOver,setDragOver] = useState(false)

    const subrules = useSelector(selectRules).filter(r=>r.parentRuleId == props.rule.ruleId)

    var ruleName = props.rule.matchText
    if(props.rule.matchWhen == "MATCH_ON_LINE"){
        ruleName = `Match line ${props.rule.matchText}`
    }

    // Validate regex fields for this rule
    function validateRuleRegex(r: GenericXlsExtractionRule): string | null {
        try {
            if(r.matchWhen == "REGEX_MATCH" && r.matchText) new RegExp(r.matchText) } catch (e) { return `Invalid match regex: ${r.matchText}` }
        try {
            if(r.extractionRegex && r.extractionRegex !== "") new RegExp(r.extractionRegex) } catch (e) { return `Invalid extraction regex: ${r.extractionRegex}` }
        return null
    }

    const invalidMsg = validateRuleRegex(props.rule)
    const isMatch = props.matchingIds.size > 0 && props.matchingIds.has(props.rule.ruleId)

    var className = ""
    if(isSelected)
        className += "Selected "

    if(props.rule.action == "LABEL")
        className += "Label "

    if(props.rule.action == "TAG")
        className += "Tag "

    if(isDraggedOver)
        className += "DragOver"

    if(invalidMsg)
        className += "Invalid "
    if(isMatch)
        className += "SearchMatch "

    const detailText = props.rule.action === "TAG" ? props.rule.tagName : props.rule.action === "LABEL" ? props.rule.selectionLabel : null

    return <>
        <li draggable="true"
            onDragStart={(evt)=>{evt.dataTransfer.setData("text/plain", props.rule.ruleId.toString());}}
            onDragOver={(e)=>{e.preventDefault();setDragOver(true)}}
            onDragLeave={(e)=>{e.preventDefault(); setDragOver(false)}}
            onDrop={(evt)=>{
                const sourceRule = +evt.dataTransfer.getData("text/plain")
                setDragOver(false)
                dispatch(moveRule({ruleId:sourceRule, moveBelowRuleWithId:props.rule.ruleId}))
            }}
            className={className}
            title={invalidMsg ?? props.rule.comment}
            onClick={()=>dispatch(setRuleToDisplay(props.rule))}>

            {invalidMsg && <Popup content={invalidMsg} on={'hover'} trigger={<Icon name="exclamation triangle" color="red" style={{marginRight: 6}}/>} />}
            {props.rule.ruleId} - {ruleName}
            {props.showDetails && detailText && (
                <span className="rule-detail" style={{fontSize: '10px', marginLeft: 8}}>
        <Icon name={props.rule.action === "TAG" ? "tag" : "pin"} size="small"/>
                    {detailText}
    </span>
            )}
        </li>
        {subrules.length>0 && <HierarchicalRuleList level={props.level+1} parent={props.rule} rules={subrules} selectedRule={props.selectedRule} showDetails={props.showDetails} matchingIds={props.matchingIds}/>}
    </>
}